import { useState } from 'react';
import { Star, ThumbsUp, MessageCircle, Award, Users, TrendingUp, CheckCircle, Heart, Calendar, User } from 'lucide-react';

const Reviews = () => {
    const [selectedRating, setSelectedRating] = useState(0);
    const [hoverRating, setHoverRating] = useState(0);
    const [reviewForm, setReviewForm] = useState({
        name: '',
        email: '',
        rating: 0,
        title: '',
        review: '',
        department: '',
        visitDate: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);
    const [activeFilter, setActiveFilter] = useState('all');

    // Sample reviews data
    const reviews = [
        {
            id: 1,
            name: 'Sarah Johnson',
            rating: 5,
            date: '2025-08-15',
            title: 'Outstanding Care and Professionalism',
            review: 'Dr. Chen and his team provided exceptional care during my recent visit. The staff was incredibly professional, and the facility is state-of-the-art. I felt heard and well-cared for throughout the entire process.',
            department: 'Cardiology',
            verified: true,
            helpful: 24
        },
        {
            id: 2,
            name: 'Michael Rodriguez',
            rating: 5,
            date: '2025-08-12',
            title: 'Excellent Pediatric Care',
            review: 'Dr. Rodriguez was amazing with my 5-year-old daughter. She made the experience comfortable and stress-free. The waiting area is child-friendly, and the staff went above and beyond to make us feel welcome.',
            department: 'Pediatrics',
            verified: true,
            helpful: 18
        },
        {
            id: 3,
            name: 'Emily Chen',
            rating: 4,
            date: '2025-08-10',
            title: 'Great Experience Overall',
            review: 'Very thorough examination and clear explanation of treatment options. The appointment scheduling system is convenient, and I barely had to wait. Minor issue with parking availability, but the medical care was excellent.',
            department: 'General Medicine',
            verified: true,
            helpful: 12
        },
        {
            id: 4,
            name: 'David Thompson',
            rating: 5,
            date: '2025-08-08',
            title: 'Life-Changing Treatment',
            review: 'After struggling with chronic pain for years, the orthopedic team here finally provided a solution that works. Dr. Johnson explained everything clearly and the rehabilitation program was perfectly tailored to my needs.',
            department: 'Orthopedics',
            verified: true,
            helpful: 31
        },
        {
            id: 5,
            name: 'Lisa Wang',
            rating: 5,
            date: '2025-08-05',
            title: 'Compassionate Emergency Care',
            review: 'Visited the emergency department late at night and received prompt, compassionate care. Despite being busy, the staff took time to explain everything and made sure I was comfortable throughout my stay.',
            department: 'Emergency Care',
            verified: true,
            helpful: 15
        },
        {
            id: 6,
            name: 'Robert Martinez',
            rating: 4,
            date: '2025-08-03',
            title: 'Professional Dermatology Service',
            review: 'Dr. Anderson was very knowledgeable and took time to address all my concerns. The treatment plan was effective, and the follow-up care was excellent. Would definitely recommend to others.',
            department: 'Dermatology',
            verified: true,
            helpful: 9
        }
    ];

    const stats = [
        { label: 'Average Rating', value: '4.8', icon: Star, color: 'from-yellow-500 to-orange-500' },
        { label: 'Total Reviews', value: '2,847', icon: MessageCircle, color: 'from-blue-500 to-blue-600' },
        { label: 'Satisfied Patients', value: '96%', icon: ThumbsUp, color: 'from-green-500 to-green-600' },
        { label: 'Recommendation Rate', value: '98%', icon: Award, color: 'from-purple-500 to-purple-600' }
    ];

    const ratingBreakdown = [
        { stars: 5, count: 2156, percentage: 76 },
        { stars: 4, count: 512, percentage: 18 },
        { stars: 3, count: 142, percentage: 5 },
        { stars: 2, count: 28, percentage: 1 },
        { stars: 1, count: 9, percentage: 0 }
    ];

    const departments = ['General Medicine', 'Cardiology', 'Pediatrics', 'Dermatology', 'Orthopedics', 'Emergency Care', 'Mental Health', 'Gynecology'];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setReviewForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleRatingSelect = (rating) => {
        setSelectedRating(rating);
        setReviewForm(prev => ({
            ...prev,
            rating: rating
        }));
    };

    const handleSubmitReview = () => {
        setIsSubmitting(true);

        setTimeout(() => {
            setIsSubmitting(false);
            setSubmitStatus('success');
            setReviewForm({
                name: '',
                email: '',
                rating: 0,
                title: '',
                review: '',
                department: '',
                visitDate: ''
            });
            setSelectedRating(0);

            setTimeout(() => {
                setSubmitStatus(null);
            }, 5000);
        }, 2000);
    };

    const renderStars = (rating, interactive = false, size = 'w-5 h-5') => {
        return [...Array(5)].map((_, index) => {
            const starRating = index + 1;
            return (
                <Star
                    key={index}
                    className={`${size} cursor-pointer transition-colors ${starRating <= (interactive ? (hoverRating || selectedRating) : rating)
                            ? 'text-yellow-400 fill-yellow-400'
                            : 'text-gray-300'
                        }`}
                    onClick={interactive ? () => handleRatingSelect(starRating) : undefined}
                    onMouseEnter={interactive ? () => setHoverRating(starRating) : undefined}
                    onMouseLeave={interactive ? () => setHoverRating(0) : undefined}
                />
            );
        });
    };

    const filteredReviews = activeFilter === 'all'
        ? reviews
        : reviews.filter(review => review.department.toLowerCase().includes(activeFilter.toLowerCase()));

    return (
        <div className='min-h-screen bg-gradient-to-br from-purple-50 to-blue-50'>
            {/* Hero Section */}
            <div className='relative bg-gradient-to-r from-purple-900 via-purple-800 to-blue-900 text-white py-20'>
                <div className='absolute inset-0 bg-black opacity-20'></div>
                <div className='relative max-w-7xl mx-auto px-4 text-center'>
                    <h1 className='text-5xl md:text-6xl font-bold mb-6'>
                        Patient Reviews
                    </h1>
                    <p className='text-xl md:text-2xl mb-8 max-w-3xl mx-auto leading-relaxed'>
                        See what our patients have to say about their healthcare experience
                    </p>
                    <div className='inline-flex items-center space-x-4 bg-white bg-opacity-20 px-6 py-3 rounded-full backdrop-blur-sm'>
                        <div className='flex items-center space-x-1'>
                            {renderStars(5, false, 'w-6 h-6')}
                        </div>
                        <span className='text-lg font-semibold'>4.8 out of 5 stars</span>
                    </div>
                </div>
            </div>

            {/* Stats Section */}
            <div className='py-16 bg-white'>
                <div className='max-w-7xl mx-auto px-4'>
                    <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8'>
                        {stats.map((stat, index) => {
                            const Icon = stat.icon;
                            return (
                                <div key={index} className='text-center group hover:transform hover:scale-105 transition-all duration-300'>
                                    <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r ${stat.color} rounded-full mb-4 group-hover:shadow-lg`}>
                                        <Icon className='w-8 h-8 text-white' />
                                    </div>
                                    <div className='text-4xl font-bold text-purple-900 mb-2'>{stat.value}</div>
                                    <div className='text-gray-600 font-medium'>{stat.label}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Rating Breakdown and Write Review */}
            <div className='py-20 bg-gradient-to-r from-purple-100 to-blue-100'>
                <div className='max-w-7xl mx-auto px-4'>
                    <div className='grid grid-cols-1 lg:grid-cols-2 gap-12'>
                        {/* Rating Breakdown */}
                        <div className='bg-white rounded-2xl shadow-xl p-8'>
                            <h2 className='text-3xl font-bold text-purple-900 mb-6'>Rating Breakdown</h2>
                            <div className='space-y-4'>
                                {ratingBreakdown.map((rating, index) => (
                                    <div key={index} className='flex items-center space-x-4'>
                                        <div className='flex items-center space-x-1 w-16'>
                                            <span className='text-sm font-medium text-gray-700'>{rating.stars}</span>
                                            <Star className='w-4 h-4 text-yellow-400 fill-yellow-400' />
                                        </div>
                                        <div className='flex-1 bg-gray-200 rounded-full h-3'>
                                            <div
                                                className='bg-gradient-to-r from-purple-600 to-blue-600 h-3 rounded-full transition-all duration-1000'
                                                style={{ width: `${rating.percentage}%` }}
                                            ></div>
                                        </div>
                                        <div className='text-sm text-gray-600 w-12 text-right'>{rating.count}</div>
                                    </div>
                                ))}
                            </div>
                            <div className='mt-8 p-6 bg-gradient-to-r from-green-50 to-green-100 rounded-xl border border-green-200'>
                                <div className='flex items-center space-x-3 mb-3'>
                                    <TrendingUp className='w-6 h-6 text-green-600' />
                                    <h3 className='text-lg font-bold text-green-800'>Patient Satisfaction Trending Up</h3>
                                </div>
                                <p className='text-green-700'>Our patient satisfaction scores have increased by 12% over the past year, reflecting our commitment to continuous improvement.</p>
                            </div>
                        </div>

                        {/* Write Review Form */}
                        <div className='bg-white rounded-2xl shadow-xl p-8'>
                            <h2 className='text-3xl font-bold text-purple-900 mb-6'>Write a Review</h2>

                            {submitStatus === 'success' && (
                                <div className='bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center space-x-3'>
                                    <CheckCircle className='w-6 h-6 text-green-600' />
                                    <div>
                                        <p className='text-green-800 font-semibold'>Review Submitted Successfully!</p>
                                        <p className='text-green-600 text-sm'>Thank you for your feedback. Your review will be published after verification.</p>
                                    </div>
                                </div>
                            )}

                            <div className='space-y-6'>
                                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                                    <div>
                                        <label className='block text-gray-700 font-semibold mb-2'>Your Name</label>
                                        <input
                                            type='text'
                                            name='name'
                                            value={reviewForm.name}
                                            onChange={handleInputChange}
                                            className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all'
                                            placeholder='Enter your name'
                                        />
                                    </div>
                                    <div>
                                        <label className='block text-gray-700 font-semibold mb-2'>Email Address</label>
                                        <input
                                            type='email'
                                            name='email'
                                            value={reviewForm.email}
                                            onChange={handleInputChange}
                                            className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all'
                                            placeholder='Enter your email'
                                        />
                                    </div>
                                </div>

                                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                                    <div>
                                        <label className='block text-gray-700 font-semibold mb-2'>Department Visited</label>
                                        <select
                                            name='department'
                                            value={reviewForm.department}
                                            onChange={handleInputChange}
                                            className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all'
                                        >
                                            <option value=''>Select Department</option>
                                            {departments.map((dept, index) => (
                                                <option key={index} value={dept}>{dept}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className='block text-gray-700 font-semibold mb-2'>Visit Date</label>
                                        <input
                                            type='date'
                                            name='visitDate'
                                            value={reviewForm.visitDate}
                                            onChange={handleInputChange}
                                            className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all'
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className='block text-gray-700 font-semibold mb-2'>Overall Rating</label>
                                    <div className='flex items-center space-x-1'>
                                        {renderStars(selectedRating, true, 'w-8 h-8')}
                                        {selectedRating > 0 && (
                                            <span className='ml-3 text-gray-600'>({selectedRating} star{selectedRating !== 1 ? 's' : ''})</span>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <label className='block text-gray-700 font-semibold mb-2'>Review Title</label>
                                    <input
                                        type='text'
                                        name='title'
                                        value={reviewForm.title}
                                        onChange={handleInputChange}
                                        className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all'
                                        placeholder='Give your review a title'
                                    />
                                </div>

                                <div>
                                    <label className='block text-gray-700 font-semibold mb-2'>Your Review</label>
                                    <textarea
                                        name='review'
                                        value={reviewForm.review}
                                        onChange={handleInputChange}
                                        rows='5'
                                        className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all resize-none'
                                        placeholder='Share your experience with us...'
                                    ></textarea>
                                </div>

                                <button
                                    onClick={handleSubmitReview}
                                    disabled={isSubmitting || !selectedRating || !reviewForm.review}
                                    className='w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold py-4 px-6 rounded-lg hover:shadow-lg transition-all duration-300 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed'
                                >
                                    {isSubmitting ? (
                                        <>
                                            <div className='w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin'></div>
                                            <span>Submitting...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Heart className='w-5 h-5' />
                                            <span>Submit Review</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filter and Reviews Section */}
            <div className='py-20 bg-white'>
                <div className='max-w-7xl mx-auto px-4'>
                    <div className='flex flex-col md:flex-row md:items-center md:justify-between mb-8'>
                        <h2 className='text-4xl font-bold text-purple-900 mb-4 md:mb-0'>Patient Reviews</h2>
                        <div className='flex flex-wrap gap-2'>
                            <button
                                onClick={() => setActiveFilter('all')}
                                className={`px-4 py-2 rounded-full font-medium transition-all ${activeFilter === 'all'
                                        ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    }`}
                            >
                                All Reviews
                            </button>
                            {departments.slice(0, 4).map((dept, index) => (
                                <button
                                    key={index}
                                    onClick={() => setActiveFilter(dept)}
                                    className={`px-4 py-2 rounded-full font-medium transition-all ${activeFilter === dept
                                            ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                        }`}
                                >
                                    {dept}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
                        {filteredReviews.map((review) => (
                            <div key={review.id} className='bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-6 hover:shadow-lg transition-all duration-300 hover:transform hover:translateY-1'>
                                <div className='flex items-start justify-between mb-4'>
                                    <div className='flex items-center space-x-3'>
                                        <div className='w-12 h-12 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center'>
                                            <User className='w-6 h-6 text-white' />
                                        </div>
                                        <div>
                                            <div className='font-bold text-purple-900'>{review.name}</div>
                                            <div className='text-sm text-gray-600'>{review.department}</div>
                                        </div>
                                    </div>
                                    {review.verified && (
                                        <div className='flex items-center space-x-1 text-green-600'>
                                            <CheckCircle className='w-4 h-4' />
                                            <span className='text-xs font-medium'>Verified</span>
                                        </div>
                                    )}
                                </div>

                                <div className='flex items-center space-x-2 mb-3'>
                                    <div className='flex items-center space-x-1'>
                                        {renderStars(review.rating)}
                                    </div>
                                    <div className='flex items-center space-x-1 text-gray-500 text-sm'>
                                        <Calendar className='w-4 h-4' />
                                        <span>{new Date(review.date).toLocaleDateString()}</span>
                                    </div>
                                </div>

                                <h3 className='font-bold text-lg text-purple-900 mb-3'>{review.title}</h3>
                                <p className='text-gray-700 leading-relaxed mb-4'>{review.review}</p>

                                <div className='flex items-center justify-between text-sm text-gray-500'>
                                    <div className='flex items-center space-x-1'>
                                        <ThumbsUp className='w-4 h-4' />
                                        <span>{review.helpful} people found this helpful</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className='text-center mt-12'>
                        <button className='bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold py-3 px-8 rounded-lg hover:shadow-lg transition-all duration-300'>
                            Load More Reviews
                        </button>
                    </div>
                </div>
            </div>

            {/* Trust Badges */}
            <div className='py-16 bg-gradient-to-r from-purple-900 via-purple-800 to-blue-900 text-white'>
                <div className='max-w-7xl mx-auto px-4 text-center'>
                    <h2 className='text-3xl font-bold mb-8'>Trusted by Thousands of Patients</h2>
                    <div className='grid grid-cols-1 md:grid-cols-3 gap-8'>
                        <div className='flex flex-col items-center'>
                            <div className='w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mb-4'>
                                <Award className='w-8 h-8' />
                            </div>
                            <h3 className='text-xl font-bold mb-2'>Excellence Award 2024</h3>
                            <p className='opacity-90'>Recognized for outstanding patient care</p>
                        </div>
                        <div className='flex flex-col items-center'>
                            <div className='w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mb-4'>
                                <Users className='w-8 h-8' />
                            </div>
                            <h3 className='text-xl font-bold mb-2'>10,000+ Happy Patients</h3>
                            <p className='opacity-90'>Serving our community since 2010</p>
                        </div>
                        <div className='flex flex-col items-center'>
                            <div className='w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mb-4'>
                                <Star className='w-8 h-8' />
                            </div>
                            <h3 className='text-xl font-bold mb-2'>4.8 Star Average</h3>
                            <p className='opacity-90'>Consistently rated by patients</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Reviews;